import React, { useMemo, useCallback, useEffect } from 'react';
import { Form, Message, Header, Table, Tab } from 'semantic-ui-react';
import numeral from 'numeral';
import IntlCurrencyInput from 'react-intl-currency-input';

function canPay(p, policies) {
	if(p.paymentInfo.isPartOfAccountBill) {
		const primaryAccountPolicy = policies.find(pp => pp.paymentInfo.displayName === p.paymentInfo.displayName && pp.paymentInfo.isPartOfAccountBill && pp.paymentInfo.isPrimaryAccountPolicy);
		const { paymentInfo: primaryPaymentInfo } = primaryAccountPolicy;

		return primaryPaymentInfo.accountOutstandingBalance >= 1 || primaryPaymentInfo.accountPayInFull > 0;
	} else {
		return p.paymentInfo.outstandingBalance >= 1 || p.paymentInfo.payInFullAmount > 0;
	}
}

const PolicySelection = ({ policies, onPolicySelectionOpen, onPolicySelectionChange, selectedPolicies, hasBalance, bypassAmountChecks, isFuture, selectedPolicyNumber, onPolicyNumberSelected }) => {
	const paymentInfos = useMemo(() => {
		const _policies = (policies || [])
				.filter((p) => (isFuture ? !!p.futurePolicy : true))
				.map(p => isFuture ? p.futurePolicy : p);

		return _policies.map((policy) => {
				const { paymentInfo } = policy;
				const selectedPolicy = selectedPolicies.find((sp) => sp.policyId === paymentInfo.policyId && sp.imageNumber === paymentInfo.imageNumber);

				return {
					canPay: canPay(policy, _policies),
					imageNumber: policy.policyImageNum,
					policyId: policy.policyId,
					amount: paymentInfo.isPartOfAccountBill ? (paymentInfo.accountOutstandingBalance >= 1 ? paymentInfo.accountOutstandingBalance : paymentInfo.accountPayInFull > 0 ? paymentInfo.accountPayInFull : 0) : paymentInfo.outstandingBalance >= 1 ? paymentInfo.outstandingBalance : paymentInfo.payInFullAmount > 0 ? paymentInfo.payInFullAmount : 10,
					paymentOption: paymentInfo.isPartOfAccountBill ? (paymentInfo.accountOutstandingBalance >= 1 ? 'Current Balance' : paymentInfo.accountPayInFull > 0 ? 'Total Balance' : 'Custom Payment') : paymentInfo.outstandingBalance >= 1 ? 'Current Balance' : paymentInfo.payInFullAmount > 0 ? 'Total Balance' : 'Custom Payment',
					...paymentInfo,
					...selectedPolicy,
				};
			})
			.filter((p) => p.canPay || bypassAmountChecks);
	}, [bypassAmountChecks, isFuture, policies, selectedPolicies]);

	useEffect(() => {
		if (selectedPolicyNumber) {
			const selectedPolicy = paymentInfos.find((pi) => pi.displayName === selectedPolicyNumber);
			if(selectedPolicy) {
				selectedPolicy.selected = true
				onPolicySelectionChange([selectedPolicy]);
			}

			if(onPolicyNumberSelected) onPolicyNumberSelected();
		} 
		
		if(onPolicySelectionOpen) onPolicySelectionOpen();
	}, [onPolicyNumberSelected, onPolicySelectionChange, onPolicySelectionOpen, paymentInfos, selectedPolicyNumber]);

	const accountItems = useMemo(() => paymentInfos.filter((paymentInfo) => paymentInfo.isPartOfAccountBill && paymentInfo.isPrimaryAccountPolicy), [paymentInfos]);
	const policyItems = useMemo(() => paymentInfos.filter((paymentInfo) => !paymentInfo.isPartOfAccountBill), [paymentInfos]);

	const total = useMemo(() => {
		const payments = paymentInfos.map((paymentInfo) => (paymentInfo.selected ? +paymentInfo.amount : 0));
		if (payments.length === 0) return 0;
		return payments.reduce((a, b) => a + b);
	}, [paymentInfos]);

	const onChange = useCallback(
		(e, { name, data, checked, value }) => {
			if (name === 'selected') {
				const alreadySelected = selectedPolicies.some((sp) => sp.displayName === data.displayName);
				if (!alreadySelected && checked) {
					data.selected = true;
					//The polich has been added
					selectedPolicies.push(data);
					onPolicySelectionChange([...selectedPolicies]);
					return;
				} else if (alreadySelected && !checked) {
					//The policy has been removed
					onPolicySelectionChange([...selectedPolicies.filter((sp) => sp.displayName !== data.displayName)]);
					return;
				}
			} else {
				const updatedPolicy = selectedPolicies.find((sp) => sp.displayName === data.displayName);

				updatedPolicy[name] = value;
				updatedPolicy.error = null;

				if (name === 'paymentOption') {
					if (value === 'Current Balance') {
						if (updatedPolicy.isPartOfAccountBill) {
							updatedPolicy.amount = updatedPolicy.accountOutstandingBalance;
						} else {
							updatedPolicy.amount = updatedPolicy.outstandingBalance;
						}
						if (updatedPolicy.amount === 0) updatedPolicy.selected = false;
					}
					if (value === 'Total Balance') {
						if (updatedPolicy.isPartOfAccountBill) {
							updatedPolicy.amount = updatedPolicy.accountPayInFull;
						} else {
							updatedPolicy.amount = updatedPolicy.payInFullAmount;
						}
						if (updatedPolicy.amount === 0) {
							onPolicySelectionChange([...selectedPolicies.filter((sp) => sp.displayName !== updatedPolicy.displayName)]);
						}
					}
					if (value === 'Custom Payment') {
						if (!updatedPolicy.isPartOfAccountBill && updatedPolicy.outstandingBalance >= 1) {
							updatedPolicy.amount = updatedPolicy.outstandingBalance;
						} else if (updatedPolicy.isPartOfAccountBill && updatedPolicy.accountOutstandingBalance >= 1) {
							updatedPolicy.amount = updatedPolicy.accountOutstandingBalance;
						} else {
							updatedPolicy.amount = 10;
						}
					}
				}

				if (name === 'amount') {
					if (!bypassAmountChecks) {
						if (updatedPolicy.paymentOption === 'Custom Payment') {
							if (!updatedPolicy.isPartOfAccountBill && updatedPolicy.outstandingBalance > 0) {
								if (updatedPolicy.amount < updatedPolicy.outstandingBalance) updatedPolicy.error = `Amount must be greater than or equal to ${numeral(updatedPolicy.outstandingBalance).format('$ 0,0.00')}`;
							} else if (updatedPolicy.isPartOfAccountBill && updatedPolicy.accountOutstandingBalance > 0) {
								if (updatedPolicy.amount < updatedPolicy.accountOutstandingBalance) updatedPolicy.error = `Amount must be greater than or equal to ${numeral(updatedPolicy.accountOutstandingBalance).format('$ 0,0.00')}`;
							} else {
								if (updatedPolicy.amount < 10) updatedPolicy.error = `Amount must be greater than or equal to $ 10.00`;
							}
						}
					}
				}

				onPolicySelectionChange([...selectedPolicies]);
			}
		},
		[bypassAmountChecks, onPolicySelectionChange, selectedPolicies]
	);

	if (!hasBalance && !bypassAmountChecks) {
		return (
			<React.Fragment>
				<Header as='h1' content='You currently have a $0 balance.' textAlign='center' />
				<Header as='h1' content='If you have any questions, please call customer service at 800.477.1660.' textAlign='center' />
			</React.Fragment>
		);
	}

	return (
		<Form className='policy-selection' widths='equal' error>
			<Table id="paymentAccountsAndPolicies">
				{accountItems.length > 0 && (
					<React.Fragment>
						<Table.Header>
							<Table.Row>
								<Table.HeaderCell>Account</Table.HeaderCell>
								<Table.HeaderCell>Policies</Table.HeaderCell>
								<Table.HeaderCell>Payment Option</Table.HeaderCell>
								<Table.HeaderCell textAlign='right'>Amount</Table.HeaderCell>
							</Table.Row>
						</Table.Header>
						<Table.Body>
							{accountItems.map((paymentInfo, key) => {
								const paymentOptions = [];

								if (paymentInfo.accountOutstandingBalance >= 1) {
									paymentOptions.push({ text: 'Current Balance', value: 'Current Balance' });
								}

								if (paymentInfo.accountPayInFull > 0) {
									paymentOptions.push({ text: 'Total Balance', value: 'Total Balance' });
								}

								paymentOptions.push({ text: 'Custom Payment', value: 'Custom Payment' });

								const rowDisabled = (paymentInfo.accountOutstandingBalance < 1 && paymentInfo.paymentOption === 'Current Balance') || (paymentInfo.accountPayInFull === 0 && paymentInfo.paymentOption === 'Total Balance');
								const paymentOptionDisabled = !paymentInfo.selected;
								const amountDisabled = rowDisabled || paymentInfo.paymentOption === 'Current Balance' || paymentInfo.paymentOption === 'Total Balance';

								return (
									<Table.Row key={key}>
										<Table.Cell>
											<Form.Checkbox name='selected' label={`${paymentInfo.displayName}`} onChange={onChange} data={paymentInfo} checked={paymentInfo.selected} disabled={rowDisabled} />
										</Table.Cell>
										<Table.Cell>
											{paymentInfos
												.filter((i) => i.isPartOfAccountBill && i.accountNumber === paymentInfo.accountNumber && i.isFuture === paymentInfo.isFuture)
												.map((paymentInfo, key) => (
													<div key={key}>{paymentInfo.policyNumber}</div>
												))}
										</Table.Cell>
										<Table.Cell>
											<Form.Dropdown name='paymentOption' selection options={paymentOptions} value={paymentInfo.paymentOption} onChange={onChange} data={paymentInfo} disabled={paymentOptionDisabled} />
										</Table.Cell>
										<Table.Cell>
											<Form.Field control={IntlCurrencyInput} name='amount' style={{ textAlign: 'right' }} value={paymentInfo.amount} disabled={amountDisabled} error={!!paymentInfo.error} onChange={(e, value) => onChange(e, { name: 'amount', data: paymentInfo, value: value })} prefix='$ ' />
										</Table.Cell>
										{paymentInfo.error ? <Message error content={paymentInfo.error} /> : null}
									</Table.Row>
								);
							})}
						</Table.Body>
					</React.Fragment>
				)}

				{policyItems.length > 0 && (
					<React.Fragment>
						<Table.Header>
							<Table.Row>
								<Table.HeaderCell>Policy</Table.HeaderCell>
								<Table.HeaderCell />
								<Table.HeaderCell>Payment Option</Table.HeaderCell>
								<Table.HeaderCell textAlign='right'>Amount</Table.HeaderCell>
							</Table.Row>
						</Table.Header>
						<Table.Body>
							{paymentInfos
								.filter((paymentInfo) => !paymentInfo.isPartOfAccountBill)
								.map((paymentInfo, key) => {
									const paymentOptions = [];
									if (paymentInfo.outstandingBalance >= 1) {
										paymentOptions.push({ text: 'Current Balance', value: 'Current Balance' });
									}

									if (paymentInfo.payInFullAmount > 0) {
										paymentOptions.push({ text: 'Total Balance', value: 'Total Balance' });
									}

									paymentOptions.push({ text: 'Custom Payment', value: 'Custom Payment' });

									const rowDisabled = (paymentInfo.outstandingBalance < 1 && paymentInfo.paymentOption === 'Current Balance') || (paymentInfo.payInFullAmount === 0 && paymentInfo.paymentOption === 'Total Balance');
									const paymentOptionDisabled = !paymentInfo.selected;
									const amountDisabled = rowDisabled || paymentInfo.paymentOption === 'Current Balance' || paymentInfo.paymentOption === 'Total Balance';

									return (
										<Table.Row key={key} name={paymentInfo.displayName}>
											<Table.Cell>
												<Form.Checkbox name='selected' label={paymentInfo.displayName} onChange={onChange} data={paymentInfo} checked={paymentInfo.selected} disabled={rowDisabled} />
											</Table.Cell>
											<Table.Cell></Table.Cell>
											<Table.Cell>
												<Form.Dropdown name='paymentOption' selection options={paymentOptions} value={paymentInfo.paymentOption} onChange={onChange} data={paymentInfo} disabled={paymentOptionDisabled} />
											</Table.Cell>
											<Table.Cell>
												<Form.Field control={IntlCurrencyInput} name='amount' style={{ textAlign: 'right' }} value={paymentInfo.amount} disabled={amountDisabled || paymentOptionDisabled} error={!!paymentInfo.error} onChange={(e, value) => onChange(e, { name: 'amount', data: paymentInfo, value: value })} prefix='$ ' />
											</Table.Cell>
											{paymentInfo.error ? <Message error content={paymentInfo.error} /> : null}
										</Table.Row>
									);
								})}
						</Table.Body>
					</React.Fragment>
				)}
				<Table.Footer>
					<Table.Row>
						<Table.HeaderCell />
						<Table.HeaderCell />
						<Table.HeaderCell textAlign='right'>Total</Table.HeaderCell>
						<Table.HeaderCell textAlign='right'>{numeral(total).format('$ 0,0.00')}</Table.HeaderCell>
					</Table.Row>
				</Table.Footer>
			</Table>
		</Form>
	);
};

export default PolicySelection;
